import { useCallback, useEffect, useRef, useState } from 'react';

// How close to the bottom still counts as parked there. Wide enough to absorb
// sub-pixel scrollHeight rounding and a stray trackpad tick, tight enough that a
// deliberate scroll up detaches on the first notch.
const BOTTOM_THRESHOLD_PX = 64;

// Keys that scroll a focused container up, and so mean the same thing a wheel
// tick up does. Only reachable while focus sits inside the transcript (a doc
// chip, a source link) — the scroller itself is not focusable.
const SCROLL_UP_KEYS = new Set(['ArrowUp', 'PageUp', 'Home']);

type Options = {
  // True while the caller is restoring a scroll position itself (an older page
  // being prepended). Following would fight that restore, so it stands down.
  isPaused?: () => boolean;
};

// Keeps a scroll container pinned to its bottom as content grows, and lets go
// the moment the user scrolls up to read — the standard chat-transcript
// contract. Attach `registerScrollNode` to the scroller and `setContentNode` to
// the growing content inside it, and wire `handleScroll` to its onScroll.
export default function useStickToBottom(options: Options = {}) {
  const { isPaused } = options;
  // The hot path — read and written on every wheel tick, scroll event and
  // resize — so it lives in a ref. `pinned` mirrors it for the one consumer that
  // has to re-render: the jump-to-bottom affordance.
  const stickRef = useRef(true);
  const [pinned, setPinned] = useState(true);
  // Nodes are held in state, not just refs, so the observer and listener effects
  // re-run when the scroller mounts. It renders conditionally (an empty
  // conversation shows no transcript at all), so a ref alone would leave them
  // bound to nothing for the whole first turn.
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  // Mirrors scrollNode for callers that read the element outside of render
  // (pagination, prepend restore) without re-subscribing to it.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;

  const registerScrollNode = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    setScrollNode(node);
  }, []);

  const setStick = useCallback((next: boolean) => {
    if (stickRef.current === next) return;
    stickRef.current = next;
    setPinned(next);
  }, []);

  // Always instant, never smooth. A smooth jump animates through every position
  // between here and the bottom, and each one arrives as a scroll event reading
  // "far from the bottom" — the exact signal that means the user scrolled away,
  // so the animation would disarm the following it was performing. A stream
  // would also outrun it, restarting the animation on every delta.
  const scrollToBottom = useCallback(() => {
    const list = scrollRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, []);

  // Re-arm following and jump. For the send paths (the user expects to land on
  // the message they just sent, even if they had scrolled up) and the
  // jump-to-bottom button.
  const pinToBottom = useCallback(() => {
    setStick(true);
    scrollToBottom();
  }, [setStick, scrollToBottom]);

  // Follow content growth. A ResizeObserver sees every height change — a
  // streamed delta, a tool step appearing, a markdown image finishing decoding,
  // the composer growing underneath and shrinking the viewport — where an effect
  // keyed on rendered state can only approximate them, and only for the growth
  // React knows it caused.
  useEffect(() => {
    if (!scrollNode || !contentNode) return undefined;
    const observer = new ResizeObserver(() => {
      if (!stickRef.current || isPausedRef.current?.()) return;
      scrollToBottom();
    });
    // The content for what it adds, the scroller for what the composer takes
    // away: a taller composer shrinks the viewport without touching the content.
    observer.observe(contentNode);
    observer.observe(scrollNode);
    return () => observer.disconnect();
  }, [scrollNode, contentNode, scrollToBottom]);

  // Detach on the user's own intent to scroll up, read from the input event
  // rather than from the scroll it causes. Scroll events are delivered
  // asynchronously and coalesced, so mid-stream a delta's follow can land
  // between the wheel tick and its scroll event and yank the viewport straight
  // back down — the "can't scroll up while it's generating" bug. The input event
  // cannot be outrun: it disarms following before the scroll it triggers.
  useEffect(() => {
    if (!scrollNode) return undefined;
    const detach = () => {
      // A gesture over a transcript that fits its viewport is not a request to
      // leave the bottom — there is nowhere else to be. Without this, a wheel
      // tick over a short chat detaches following (and offers a jump-to-bottom
      // button) on a scroll that never happened and never fires a scroll event
      // to re-arm from.
      if (scrollNode.scrollHeight <= scrollNode.clientHeight) return;
      setStick(false);
    };
    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) detach();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (SCROLL_UP_KEYS.has(event.key)) detach();
    };
    // A finger moving down drags the content down — it scrolls up, the same
    // intent a negative wheel delta carries. Compared against the previous move
    // rather than the gesture's start so a drag that reverses mid-way is read as
    // it happens, and so an upward flick (scrolling further down, toward the
    // bottom the user is already following) never detaches.
    let lastTouchY: number | null = null;
    const handleTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? null;
    };
    const handleTouchMove = (event: TouchEvent) => {
      const touchY = event.touches[0]?.clientY;
      if (touchY === undefined) return;
      if (lastTouchY !== null && touchY > lastTouchY) detach();
      lastTouchY = touchY;
    };
    scrollNode.addEventListener('wheel', handleWheel, { passive: true });
    scrollNode.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    scrollNode.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });
    scrollNode.addEventListener('keydown', handleKeyDown);
    return () => {
      scrollNode.removeEventListener('wheel', handleWheel);
      scrollNode.removeEventListener('touchstart', handleTouchStart);
      scrollNode.removeEventListener('touchmove', handleTouchMove);
      scrollNode.removeEventListener('keydown', handleKeyDown);
    };
  }, [scrollNode, setStick]);

  // Re-arm when the user comes back to the bottom, and cover the scroll gestures
  // that have no input event of their own (dragging the scrollbar). Reading the
  // metrics here rather than in a render-time effect keeps the streaming path off
  // the synchronous-layout path.
  const handleScroll = useCallback(() => {
    const list = scrollRef.current;
    if (!list) return;
    const distanceFromBottom =
      list.scrollHeight - list.scrollTop - list.clientHeight;
    setStick(distanceFromBottom <= BOTTOM_THRESHOLD_PX);
  }, [setStick]);

  return {
    // Attach to the scroll container: `ref={registerScrollNode}`.
    registerScrollNode,
    // Attach to the growing content inside it: `ref={setContentNode}`.
    setContentNode,
    // The scroll container, for callers that read it outside of render.
    scrollRef,
    // The attached nodes themselves, for callers that need to observe the same
    // geometry this hook follows (the transcript's older-page viewport fill).
    // State, not refs, so those callers re-subscribe when the nodes mount.
    scrollNode,
    contentNode,
    // Whether the viewport is currently following the bottom.
    pinned,
    // Re-arm following and jump to the bottom.
    pinToBottom,
    // Wire to the scroll container's onScroll.
    handleScroll,
  };
}
